import { useEffect, useState } from 'react';
import { useT } from '../lib/i18n';

function format(totalSeconds: number): string {
  const s = Math.abs(totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * A countdown to a moment, as a pill.
 *
 * It ticks locally against a deadline that its caller worked out, so nothing
 * has to send clock messages: a match derives the deadline from its event log
 * and two tablets watching agree to the second, and a practice session derives
 * it from when the player pressed start.
 *
 * Running out is announced rather than enforced. Whether an over-running match
 * is abandoned, conceded or simply played out is a refereeing decision, and
 * the scoreboard has no business making it; a practice session that runs long
 * is nobody's business at all. So the clock goes red and starts counting up.
 */
export function Countdown({ deadline, title }: { deadline: Date | null; title?: string }) {
  const t = useT();
  const [now, setNow] = useState(() => Date.now());
  const at = deadline?.getTime() ?? null;

  useEffect(() => {
    if (at === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [at]);

  if (at === null) return null;

  const remaining = Math.round((at - now) / 1000);
  const over = remaining <= 0;
  const urgent = !over && remaining <= 60;

  return (
    <div
      className={[
        'pointer-events-auto flex items-center gap-2 rounded-full px-3.5 py-1',
        over
          ? 'bg-ball-red/80 ring-1 ring-inset ring-white/20'
          : urgent
            ? 'bg-ball-red/25 ring-1 ring-inset ring-ball-red/40'
            : 'bg-white/[0.05] ring-1 ring-inset ring-white/10',
      ].join(' ')}
      title={title}
    >
      <span className="hud-label whitespace-nowrap">{over ? t('clock.up') : t('clock.left')}</span>
      <span className="num text-lg font-semibold leading-none tracking-[-0.01em] sm:text-xl">
        {over ? `+${format(remaining)}` : format(remaining)}
      </span>
    </div>
  );
}
